import React, { useEffect, useState } from "react";
import { RefreshCw, HardDrive, ArrowRightLeft, Play, RotateCcw, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; }
};

// Libellé + couleur d'un état du pipeline (voir replay_offload._row_state côté serveur).
const STATE_UI = {
  pending: { label: "Chez Cloudflare · en attente", bg: "#FEF3C7", color: "#92400E" },
  downloading: { label: "Copie MP4 chez Cloudflare…", bg: "#DBEAFE", color: "#1D4ED8" },
  encoding: { label: "Encodage Bunny…", bg: "#DBEAFE", color: "#1D4ED8" },
  swapped: { label: "Sur Bunny · Cloudflare effacée à", bg: "#DCFCE7", color: "#166534" },
  done: { label: "Transférée sur Bunny", bg: "#DCFCE7", color: "#166534" },
  failed: { label: "Échec", bg: "#FEE2E2", color: "#B91C1C" },
  elsewhere: { label: "Déjà hors Cloudflare", bg: "#F3F4F6", color: "#4B5563" },
  ineligible: { label: "Non transférable", bg: "#F3F4F6", color: "#4B5563" },
};

// Liste des dernières rediffusions + « Relancer » (GET /admin/live/offload/replays, POST …/{id}/retry).
const OffloadReplays = ({ canManage, onChanged }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/live/offload/replays").then(({ data }) => setRows(data.replays || [])).catch(() => setRows([]));
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, []);
  const retry = async (r) => {
    setBusy(r.id);
    try {
      await api.post(`/admin/live/offload/${r.id}/retry`);
      toast.success("Rediffusion relancée");
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || e?.friendlyMessage || "Erreur"); }
    finally { setBusy(null); }
  };
  if (rows === null) return <div className="py-3 text-center"><Loader2 size={16} className="animate-spin mx-auto" /></div>;
  if (rows.length === 0) return <div className="text-xs py-2" style={{ color: "var(--yalola-text-muted)" }} data-testid="live-offload-empty">Aucune rediffusion enregistrée.</div>;
  return (
    <div className="mt-2 divide-y rounded-xl border overflow-hidden" style={{ borderColor: "var(--yalola-border)" }} data-testid="live-offload-list">
      {rows.map((r) => {
        const ui = STATE_UI[r.state] || STATE_UI.ineligible;
        const detail = r.state === "swapped" ? fmtDate(r.detail) : r.state === "done" && r.detail === "cf_delete_failed" ? "copie Cloudflare NON effacée" : r.detail;
        return (
          <div key={r.id} className="flex items-center gap-3 px-3 py-2 text-xs bg-white" data-testid={`live-offload-row-${r.id}`} data-state={r.state}>
            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate" style={{ color: "var(--yalola-text)" }}>{r.title} <span className="font-normal" style={{ color: "var(--yalola-text-muted)" }}>· {r.host_name}</span></div>
              <div style={{ color: "var(--yalola-text-muted)" }}>{fmtDate(r.ended_at)} · {r.minutes} min{r.attempts > 1 ? ` · ${r.attempts} tentatives` : ""}</div>
            </div>
            <div className="text-right max-w-[55%]">
              <span className="inline-block px-2 py-0.5 rounded-full font-semibold" style={{ background: ui.bg, color: ui.color }} data-testid={`live-offload-state-${r.id}`}>{ui.label}{r.state === "swapped" ? ` ${detail}` : ""}</span>
              {detail && r.state !== "swapped" && <div className="mt-0.5" style={{ color: r.state === "failed" ? "#B91C1C" : "var(--yalola-text-muted)" }} data-testid={`live-offload-detail-${r.id}`}>{detail}</div>}
            </div>
            {r.can_retry && canManage && (
              <Button type="button" variant="outline" size="sm" className="h-7 px-2 shrink-0" onClick={() => retry(r)} disabled={busy === r.id} data-testid={`live-offload-retry-${r.id}`}>
                {busy === r.id ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} Relancer
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
};

const gaugeColor = (pct, threshold) => {
  if (pct == null) return "var(--yalola-text-muted)";
  if (pct >= 100) return "#B91C1C";
  if (pct >= threshold) return "#C2410C";
  if (pct >= threshold - 20) return "#CA8A04";
  return "#15803D";
};

// Admin → Directs : jauge des minutes Cloudflare Stream + pipeline « Rediffusions sur Bunny ».
// `cfg`/`setCfg` = live settings (replay_offload_bunny, storage_alert_pct) saved by the parent.
export const LiveStorageCard = ({ cfg, setCfg, canManage }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/admin/live/storage${refresh ? "?refresh=1" : ""}`);
      setData(d);
      if (refresh) toast.success("Jauge Cloudflare actualisée");
    } catch (e) { if (refresh) toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(false); }, []);
  // « Lancer maintenant » : un tour du pipeline tout de suite (sinon le planificateur passe chaque minute).
  const runNow = async () => {
    setRunning(true);
    try {
      const { data: out } = await api.post("/admin/live/offload/run");
      if (out.skipped) toast.error("Active d'abord « Rediffusions sur Bunny » puis Enregistre.");
      else toast.success(`Pipeline lancé · ${out.started} démarrée(s) · ${out.swapped} basculée(s) · ${out.done} terminée(s) · ${out.failed} échec(s)`);
      await load(false);
      setDetailOpen(true);
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setRunning(false); }
  };
  const u = data?.usage;
  const pct = u?.pct ?? null;
  const threshold = Math.max(50, Math.min(95, parseInt(cfg.storage_alert_pct, 10) || 80));
  const color = gaugeColor(pct, threshold);
  const off = data?.offload;
  return (
    <div className="yalola-card p-5" data-testid="live-storage-card" style={{ borderLeft: `3px solid ${color}` }}>
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="font-bold font-display text-lg flex items-center gap-2"><HardDrive size={18} /> Stockage Cloudflare Stream</div>
        <Button type="button" variant="outline" size="sm" onClick={() => load(true)} disabled={loading} data-testid="live-storage-refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Actualiser
        </Button>
      </div>
      <p className="text-sm mb-3" style={{ color: "var(--yalola-text-muted)" }}>
        Chaque minute de rediffusion stockée est facturée (5 $ / 1 000 min / mois, par palier prépayé). À <b>100 %</b>, Cloudflare refuse les nouveaux enregistrements <b>et le démarrage des directs</b>. Vérifiée chaque heure ; les super admins sont prévenus (notification + e-mail) au seuil ci-dessous, puis à 100 %.
      </p>

      {u ? (
        <div className="rounded-xl px-4 py-3 mb-4" style={{ background: "var(--yalola-surface-alt)" }} data-testid="live-storage-gauge" data-pct={pct ?? ""}>
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div className="text-2xl font-bold font-display" style={{ color }} data-testid="live-storage-minutes">
              {Math.round(u.minutes).toLocaleString("fr-FR")} <span className="text-sm font-normal" style={{ color: "var(--yalola-text-muted)" }}>/ {Number(u.limit).toLocaleString("fr-FR")} minutes</span>
            </div>
            <div className="text-sm font-semibold" style={{ color }} data-testid="live-storage-pct">{pct == null ? "—" : `${pct.toFixed(0)} %`}</div>
          </div>
          <div className="h-2.5 rounded-full overflow-hidden mt-2" style={{ background: "rgba(0,0,0,0.08)" }}>
            <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${Math.min(100, pct ?? 0)}%`, background: color }} />
          </div>
          <div className="flex justify-between text-xs mt-1.5" style={{ color: "var(--yalola-text-muted)" }}>
            <span data-testid="live-storage-videos">{u.videos} vidéo{u.videos > 1 ? "s" : ""} chez Cloudflare</span>
            <span>Vérifié le {fmtDate(u.checked_at)}</span>
          </div>
        </div>
      ) : (
        <div className="rounded-xl px-4 py-3 mb-4 text-sm" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }} data-testid="live-storage-empty">
          {loading ? "Lecture de Cloudflare…" : "Jauge indisponible : Cloudflare Stream n'est pas configuré ou ne répond pas."}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-xl px-4 py-3" style={{ background: "var(--yalola-surface-alt)" }}>
          <Label className="text-xs">Seuil d'alerte (%)</Label>
          <Input type="number" min={50} max={95} value={cfg.storage_alert_pct ?? 80} disabled={!canManage}
                 onChange={(e) => setCfg((c) => ({ ...c, storage_alert_pct: e.target.value }))} data-testid="live-storage-threshold" />
          <p className="text-xs mt-1.5" style={{ color: "var(--yalola-text-muted)" }}>Entre 50 et 95 %. Une relance est envoyée chaque jour tant que le seuil est dépassé.</p>
        </div>
        <div className="rounded-xl px-4 py-3" style={{ background: "var(--yalola-surface-alt)" }}>
          <div className="flex items-center justify-between gap-3">
            <Label className="text-xs flex items-center gap-1.5"><ArrowRightLeft size={13} /> Rediffusions sur Bunny</Label>
            <Switch checked={!!cfg.replay_offload_bunny} disabled={!canManage}
                    onCheckedChange={(v) => setCfg((c) => ({ ...c, replay_offload_bunny: v }))} data-testid="live-offload-switch" />
          </div>
          <p className="text-xs mt-1.5" style={{ color: "var(--yalola-text-muted)" }}>
            Après chaque direct, la rediffusion est copiée vers Bunny Stream (facturé au Go, ≈ 10× moins cher) puis effacée chez Cloudflare 6 h plus tard. Tout se passe entre serveurs, en tâche de fond : <b>aucun ralentissement</b>, le direct et la rediffusion restent disponibles pendant l'opération.
          </p>
          {off && (
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs mt-2" data-testid="live-offload-summary" data-enabled={off.enabled ? "1" : "0"}>
              <span>À transférer : <b>{off.pending}</b></span>
              <span>En cours : <b>{off.in_flight + off.swapped}</b></span>
              <span>Transférées : <b>{off.done}</b></span>
              <span style={{ color: off.failed ? "#B91C1C" : undefined }}>Échecs : <b>{off.failed}</b></span>
              <span>Minutes libérées : <b>{off.freed_minutes}</b></span>
            </div>
          )}
          {off && (
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {canManage && (
                <Button type="button" size="sm" className="pill-primary rounded-full h-8" onClick={runNow} disabled={running || !off.enabled} title={off.enabled ? "Un tour du pipeline tout de suite (3 rediffusions max à la fois)" : "Active l'interrupteur puis Enregistre"} data-testid="live-offload-run">
                  {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Lancer maintenant
                </Button>
              )}
              <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => setDetailOpen((v) => !v)} data-testid="live-offload-detail-toggle">
                {detailOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Détail des rediffusions
              </Button>
              {!off.enabled && <span className="text-[11px]" style={{ color: "#B45309" }} data-testid="live-offload-disabled-hint">Interrupteur éteint : rien ne se transfère tant qu'il n'est pas activé et enregistré.</span>}
            </div>
          )}
        </div>
      </div>
      {off && detailOpen && <OffloadReplays canManage={canManage} onChanged={() => load(false)} />}
    </div>
  );
};
